/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect } from 'react'
import { DialogActions, Typography, Divider, Box, Drawer, IconButton, useTheme } from '@mui/material'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType, ButtonSize } from '../../../../components/GenericButton/styles'
import { notification } from '../../../../components/Notifications'
import ComparisonsSection from './ComparsionSection/ComparisonsSection'
import { black, shade } from '../../../../colors'
import { chartSettingsSwitches, comparisonSchema } from '../constants'
import { SwitchElement } from 'react-hook-form-mui'
import SlicesSection from './SlicesSections/SlicesSection'
import { getDefaultSettings, getFinalReportPayload, isBaseReportDisplaySettings, normalizeDisplaySettings, scopedSliceKey } from '../helper'
import { useReportsContext, ReportsActionType } from '../../ReportsContext'
import { type DisplaySettings } from '../../types'
import { useCurrentUserAttributeScope } from '../../hooks/useCurrentUserAttributeScope'

export interface DisplayCustomizationDrawerProps {
  isModalOpen: boolean
  onClose: () => void
  fetchReports: (params: Record<string, any>) => Promise<any>
  onSubmitDisplaySettings?: (settings: DisplaySettings) => Promise<void>
}

const DisplayCustomizationDrawer: React.FC<DisplayCustomizationDrawerProps> = ({
  isModalOpen,
  onClose,
  fetchReports,
  onSubmitDisplaySettings
}) => {
  const theme = useTheme()
  const { dispatch, displaySettings } = useReportsContext()
  const { appliedAttributeScope } = useCurrentUserAttributeScope()
  const scopedKeys = React.useMemo(
    () => new Set(appliedAttributeScope.courseAttributes.map(e => scopedSliceKey(e.title, e.value))),
    [appliedAttributeScope]
  )
  const hasAttributeScope =
    appliedAttributeScope.courseAttributes.length > 0 ||
    appliedAttributeScope.facultyAttributes.length > 0

  const methods = useForm<any>({
    defaultValues: displaySettings || getDefaultSettings(),
    resolver: yupResolver(comparisonSchema)
  })

  const { handleSubmit, control, setValue, watch, reset } = methods

  useEffect(() => {
    if (!isModalOpen) {
      return
    }

    const nextSettings = normalizeDisplaySettings(displaySettings)
    reset(nextSettings)
  }, [displaySettings, isModalOpen, reset])

  const onSubmit = async (data: DisplaySettings): Promise<void> => {
    const normalizedData = normalizeDisplaySettings(data)

    if (onSubmitDisplaySettings) {
      try {
        await onSubmitDisplaySettings(normalizedData)
        onClose()
      } catch (error) {
        notification('Error submitting data', { variant: 'error' })
      }
      return
    }

    const formattedData = getFinalReportPayload(undefined, normalizedData, scopedKeys)

    dispatch({ type: ReportsActionType.SET_DISPLAY_SETTINGS, payload: normalizedData })
    dispatch({ type: ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED, payload: !isBaseReportDisplaySettings(normalizedData) })

    // Close first so the report page can immediately transition into the
    // pending badges + skeleton state instead of waiting on the network.
    onClose()

    try {
      await fetchReports(formattedData)
    } catch (error) {
      notification('Error submitting data', { variant: 'error' })
    }
  }

  const comparisons = watch('comparisons') || []
  const slices = watch('slices') || []

  const isSingleComparison = comparisons?.[0]?.comparison !== null
  const isSliceSelected = slices.length > 0

  useEffect(() => {
    if (isSingleComparison || isSliceSelected) {
      setValue('chartSettings.percentage', true)
    } else {
      setValue('chartSettings.percentage', false)
    }
  }, [isSingleComparison, slices, comparisons, setValue])

  const handleClearAllFilters = (): void => {
    chartSettingsSwitches.forEach(({ name }) => {
      setValue(`chartSettings.${name}`, false)
    })
    setValue('comparisons', [{ compareBy: null, comparison: null, comparisonOption: null }])
    setValue('slices', [])
    setValue('responseFilter', null)
  }

  return (
    <Drawer
      anchor="right"
      open={isModalOpen}
      onClose={onClose}
      keepMounted={true}
      PaperProps={{
        sx: {
          width: theme.spacing(120),
          padding: `${theme.spacing(0, 1.5)}`,
          mb: 5,
          overflowX: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: 3,
          py: 2
        }}
      >
        <Box>
          <Typography variant="h6" color={black[100]} fontSize={'24px'} fontWeight={700}>
            Report Insights
          </Typography>
          {hasAttributeScope && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing Report Insights your permissions allow — limited to your Attribute and Academic Unit access.
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ mt: -1 }}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" flexDirection="column" height="100%" justifyContent={'space-between'}>
            <Box sx={{ overflowY: 'auto', padding: theme.spacing(0, 6) }}>
              <Box display={'flex'} flexDirection={'column'} gap={3} sx={{ py: 2 }}>
                <ComparisonsSection
                  name="comparisons"
                  control={control}
                />
                <SlicesSection name="slices" />
                <Divider sx={{ background: shade[650], my: 3 }} />
                <Typography fontWeight={600} color={black[100]}>Chart Settings</Typography>
                <Box sx={{ px: 3 }}>
                  {chartSettingsSwitches.map(({ label, name }) => {
                    const isPercentage = name === 'percentage'
                    const forceChecked = isPercentage && (isSingleComparison || isSliceSelected)

                    return (
                      <Controller
                        key={name}
                        name={`chartSettings.${name}`}
                        control={control}
                        render={({ field }) => (
                          <SwitchElement
                            {...field}
                            label={label}
                            checked={forceChecked ? true : field.value}
                            disabled={forceChecked ? true : field.disabled}
                          />
                        )}
                      />
                    )
                  })}
                </Box>
              </Box>
            </Box>

            <DialogActions
              sx={{
                position: 'sticky',
                bottom: 0,
                right: 0,
                zIndex: 10,
                backgroundColor: 'white',
                py: 4,
                px: 3,
                borderTop: `1px solid ${shade[650]}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <GenericButton
                label='Reset Filters'
                buttonType={ButtonType.Ghost}
                buttonSize={ButtonSize.Small}
                onClick={handleClearAllFilters}
              />
              <Box display="flex" gap={2} alignItems="center">
                <GenericButton
                  label="Cancel"
                  buttonType={ButtonType.Ghost}
                  buttonSize={ButtonSize.Small}
                  onClick={onClose}
                />
                <GenericButton
                  type="submit"
                  label="Go"
                  buttonType={ButtonType.Primary}
                  buttonSize={ButtonSize.Small}
                />
              </Box>
            </DialogActions>
          </Box>
        </form>
      </FormProvider>
    </Drawer>
  )
}

export default DisplayCustomizationDrawer
